import React, {
  useEffect,
  useCallback,
  useState,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { autorun } from 'mobx';
import { compose } from 'recompose';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import moment from 'moment-timezone';
import FullCalendar from '@fullcalendar/react';
import * as Sentry from '@sentry/browser';
import format from 'string-template';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';

import ExternalCoachContext from '../../../../context/ExternalCoachContext';
import FirebaseContext from '../../../../../context/FirebaseContext';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import ManageProgramContext from '../../../../context/ManageProgramContext';
import WorkoutAssignment, { workoutAssignmentStatuses } from '../../../../../Model/WorkoutAssignment';
import ScheduledAssignment, { ScheduledAssignmentStatus } from '../../../../../Model/ScheduledAssignment';
import GameplaySession from '../../../../../Model/GameplaySession';
import User from '../../../../../Model/User';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useBeforeUnloadGuard from '../../../../../hooks/useBeforeUnloadGuard';
import useToast from '../../../../hooks/useToast';
import useLogger from '../../../../../hooks/useLogger';
import { ActionType } from '../../../../pages/WorkoutsManager/Workouts/utils';
import ConfirmDialog from '../../../ConfirmDialog';
import LoadingOverlay from '../../../LoadingOverlay';
import WorkoutEditorModal from '../../../WorkoutEditorModal';
import config from '../../../../../config';
import { DateFormat } from '../../../../../utils/date';
import { CoachingActivity } from '../../../../../utils/log';
import { UserFacingError } from '../../../../../utils/error';

import MobileViewModal from '../MobileView';

import TrackAssignmentModal from '../../../../pages/ActivityFeed/components/TrackAssignmentModal';
import WorkoutAssignmentModal from './components/WorkoutAssignmentModal';
import WorkoutDeletionModal from './components/WorkoutDeletionModal';
import ProgramAssignmentModal from './components/ProgramAssignmentModal';
import ProgramInfo from './components/ProgramInfo';
import EventContent from './components/EventContent';
import CalendarDayView from './components/CalendarDayView';
import ScheduledDraftEventContent from './components/ScheduledDraftEventContent';

import getScheduledDrafts from './utils/scheduledDrafts';
import {
  CalendarWrapper,
  CalendarControlsRow,
  ScheduledBannerContainer,
  ScheduledRow,
  ScheduledRowIcon,
  ScheduledRowMessage,
  ScheduledRowName,
  ScheduledRowType,
  ScheduledRowTime,
  ScheduledDeleteButton,
  OnHoldBadge,
} from './styles';
import texts from './texts.json';
import './assets/css/styles.css';

const WORKOUT_ASSIGNMENT = 'workoutAssignment';
const WORKOUT_ASSIGNMENT_DETAILS = 'workoutAssignmentDetails';

/**
 * The banner copy for a scheduled assignment, which tells the coach how many of its
 * workouts are on the calendar as editable drafts. There are none to point at when
 * the assignment is held for review, or when its workouts cannot be worked out yet —
 * an auto track assignment only picks its track once it fires.
 *
 * @param {number} [draftCount] - How many drafts the assignment put on the calendar.
 * @returns {string} The copy to show after the assignment name.
 */
const getScheduledBannerMessage = (draftCount) => {
  if (!draftCount) {
    return texts.scheduledBanner.noDraftsMessage;
  }
  return draftCount === 1
    ? texts.scheduledBanner.singleWorkoutMessage
    : format(texts.scheduledBanner.message, { count: draftCount });
};

const WorkoutCalendar = ({
  user,
  userDoc,
  coachId,
}) => {
  const [workoutAssignments, setWorkoutAssignments] = useState({ docs: [] });
  const [showLoading, setShowLoading] = useState(true);
  const [showWorkoutAssignmentModal, setShowWorkoutAssignmentModal] = useState(false);
  const [showDeleteWorkoutModal, setShowDeleteWorkoutModal] = useState(false);
  const [showWorkoutDetailsPopup, setShowWorkoutDetailsPopup] = useState(false);
  const [showWorkoutEditorModal, setShowWorkoutEditorModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [futureLikeAssignments, setFutureLikeAssignments] = useState([]);
  const [copiedWorkout, setCopiedWorkout] = useState(null);
  const [showProgramAssignmentModal, setShowProgramAssignmentModal] = useState(false);
  const [showTrackAssignmentModal, setShowTrackAssignmentModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [hoverDate, setHoverDate] = useState(null);
  const [mobileUrl, setMobileUrl] = useState('');
  const [visibleDateRange, setVisibleDateRange] = useState(null);
  const [workoutAssignmentsCollection, setWorkoutAssignmentsCollection] = useState(null);
  const [pendingScheduledCollection, setPendingScheduledCollection] = useState(null);
  const [pendingScheduledDocs, setPendingScheduledDocs] = useState([]);
  const [scheduledToCancel, setScheduledToCancel] = useState(null);
  const [scheduledDrafts, setScheduledDrafts] = useState([]);
  const [showScheduledWorkouts, setShowScheduledWorkouts] = useState(true);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [isSavingPaste, setIsSavingPaste] = useState(false);

  const isComponentMountedRef = useComponentMounted();
  useBeforeUnloadGuard(isSavingPaste);
  const { showToast } = useToast();
  const {
    coachDoc,
    products,
  } = useContext(ExternalCoachContext);
  const {
    userId: loggedInUserId,
  } = useContext(LoggedInUserContext);
  const { coachPrograms } = useContext(ManageProgramContext);
  const { firebase } = useContext(FirebaseContext);
  const { logCoachingActivity } = useLogger();

  useEffect(() => {
    const loadPendingScheduled = async () => {
      const collection = await ScheduledAssignment.getPendingByUser(user, coachId);
      if (isComponentMountedRef.current) {
        // The collection is what the autorun below observes: keeping it is what turns the
        // one-shot fetch into a live read, so a cancel or an edit reaches the calendar.
        setPendingScheduledCollection(collection);
        setPendingScheduledDocs(collection.docs);
      }
    };
    loadPendingScheduled();
  }, [user, coachId, isComponentMountedRef]);

  // Read the collection through an autorun, the same way workout assignments are read
  // below, so an assignment scheduled from one of the modals — or cancelled, or edited
  // — reaches the banner and the drafts without the coach reloading the page.
  useEffect(() => {
    if (!pendingScheduledCollection) return undefined;

    return autorun(() => {
      const docs = pendingScheduledCollection.hasDocs
        ? pendingScheduledCollection.docs.map((doc) => {
          Boolean(doc.data); // access doc.data so MobX tracks per-doc changes (overrides trigger re-sync)
          return doc;
        })
        : [];
      if (isComponentMountedRef.current) {
        setPendingScheduledDocs(docs);
      }
    });
  }, [pendingScheduledCollection, isComponentMountedRef]);

  /**
   * Workouts of an assignment that is scheduled but has not fired yet, so the coach
   * can see and edit them before they reach the client (S2-7706). Assignments held
   * for AC review are left out: their workouts can no longer be edited, since the
   * review — not the coach — decides what gets written.
   */
  useEffect(() => {
    // Programs and workouts are read per assignment, so a slower earlier request can
    // resolve after a newer one — it must not put back drafts of an assignment that
    // has since been cancelled.
    let isLatestRequest = true;
    const loadScheduledDrafts = async () => {
      const drafts = await getScheduledDrafts(
        pendingScheduledDocs.filter(({ status }) => status === ScheduledAssignmentStatus.PENDING),
      );
      if (isLatestRequest && isComponentMountedRef.current) {
        setScheduledDrafts(drafts);
      }
    };
    loadScheduledDrafts();
    return () => {
      isLatestRequest = false;
    };
  }, [pendingScheduledDocs, isComponentMountedRef]);

  const scheduledSendTimes = useMemo(() => pendingScheduledDocs.reduce((sendTimes, doc) => ({
    ...sendTimes,
    [doc.id]: coachDoc?.timeZone
      ? moment(doc.scheduledFor?.toDate?.()).tz(coachDoc.timeZone)
        .format(DateFormat.FRIENDLY_DATETIME_WITH_TZ)
      : moment(doc.scheduledFor?.toDate?.()).format(DateFormat.SHORT_MONTH_DAY_TIME),
  }), {}), [pendingScheduledDocs, coachDoc]);

  const draftCountByAssignmentId = useMemo(() => scheduledDrafts.reduce((counts, draft) => ({
    ...counts,
    [draft.scheduledAssignmentId]: (counts[draft.scheduledAssignmentId] || 0) + 1,
  }), {}), [scheduledDrafts]);

  const handleCancelScheduledAssignment = useCallback(async () => {
    if (!scheduledToCancel) return;
    setShowLoading(true);
    try {
      const response = await firebase.remote('workout-cancelScheduledAssignmentEndpoint', {
        scheduledAssignmentId: scheduledToCancel.id,
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error cancelling assignment');
      }
      showToast(texts.scheduledDeleted);
    } catch (error) {
      showToast(texts.scheduledDeleteError, { error: true });
    } finally {
      setScheduledToCancel(null);
      setShowLoading(false);
    }
  }, [firebase, showToast, scheduledToCancel]);

  useEffect(() => {
    const loadWorkouts = async () => {
      const workoutCollection = await WorkoutAssignment.getWorkoutAssignmentsForUser(user, coachId);
      if (isComponentMountedRef.current) {
        setWorkoutAssignmentsCollection(workoutCollection);
        setWorkoutAssignments(workoutCollection);
        setShowLoading(false);
      }
    };
    loadWorkouts();
  }, [
    user,
    coachId,
    isComponentMountedRef,
  ]);

  // Use autorun to reactively sync state with collection changes
  useEffect(() => {
    if (!workoutAssignmentsCollection) return undefined;

    const disposer = autorun(() => {
      const docs = workoutAssignmentsCollection.hasDocs
        ? workoutAssignmentsCollection.docs.map((doc) => {
          Boolean(doc.data); // access doc.data so MobX tracks per-doc changes (edits trigger re-sync)
          return doc;
        })
        : [];
      if (isComponentMountedRef.current) {
        setWorkoutAssignments({ ...workoutAssignmentsCollection, docs });
      }
    });

    return disposer;
  }, [workoutAssignmentsCollection, isComponentMountedRef]);

  const deleteWorkoutAssignment = useCallback(async (workoutAssignment, closePopup) => {
    try {
      closePopup();
      const likeAssignments = workoutAssignments.docs.filter((doc) => (
        doc.name === workoutAssignment.name
        && moment(doc.startDate.toDate()).isSameOrAfter(workoutAssignment.startDate.toDate())
      ));
      const hasMoreLikeAssignments = likeAssignments.length > 1;
      // Show the deletion modal if the assignment is in a program or has similar assignments in the future
      if (workoutAssignment.programId || hasMoreLikeAssignments) {
        if (hasMoreLikeAssignments) {
          setFutureLikeAssignments(likeAssignments);
        }
        setSelectedWorkout(workoutAssignment);
        setShowDeleteWorkoutModal(true);
      } else {
        setShowLoading(true);
        const gameplaySessions = await GameplaySession.getLatestGameplaySessionCollection(
          workoutAssignment.user, workoutAssignment.id,
        );
        if (workoutAssignment.status === workoutAssignmentStatuses.ASSIGNED && !gameplaySessions.hasDocs) {
          logCoachingActivity(CoachingActivity.DELETED_WORKOUT_ASSIGNMENT, {
            workoutAssignmentId: workoutAssignment.id,
            clientId: workoutAssignment.user,
          });
          setWorkoutAssignments((prev) => ({
            ...prev,
            docs: prev.docs.filter((d) => d.id !== workoutAssignment.id),
          }));
          try {
            await workoutAssignment.delete();
            showToast(texts.deleteSuccess);
          } catch (deleteError) {
            setWorkoutAssignments((prev) => ({
              ...prev,
              docs: [...prev.docs, workoutAssignment],
            }));
            throw deleteError;
          }
        } else {
          showToast(texts.inProgressWorkout, { warning: true });
        }
        setShowLoading(false);
      }
    } catch (error) {
      const errMsg = format(texts.deleteError, { msg: error.message });
      showToast(errMsg, { error: true });
      Sentry.captureMessage(errMsg, {
        extra: {
          userId: user,
          workoutAssignmentId: workoutAssignment.id || 'undefined',
        },
      });
      setShowLoading(false);
    }
  }, [
    workoutAssignments,
    logCoachingActivity,
    showToast,
    user,
  ]);

  // Extracted so the memo dependency is a plain value the linter can check.
  const showProductName = !!userDoc?.hasMultipleActiveProducts;

  // A workout copied from before this feature has none, and an unattributed one answers for every product.
  const soleActiveProductId = userDoc?.activeProductIds?.length === 1
    ? userDoc.activeProductIds[0]
    : undefined;

  const copyWorkoutAssignment = useCallback(async (workout, closePopup) => {
    closePopup();
    setCopiedWorkout(workout);
  }, []);

  /*
    The calendar listener shows the pasted workout before Firestore acknowledges the write, and the web
    app has no offline persistence, so a refresh before the ack silently drops the assignment. Keep the
    overlay and the unload guard up until the write settles.
  */
  const onPasteWorkout = useCallback(async (dateStr) => {
    // A workout copied from a product the client no longer holds would label the paste with an ended product.
    const isCopiedProductHeld = !!userDoc?.activeProductIds?.includes(copiedWorkout.productId);
    const productId = isCopiedProductHeld ? copiedWorkout.productId : soleActiveProductId;
    const productName = (isCopiedProductHeld && copiedWorkout.productName)
      || products?.find(({ id }) => id === productId)?.name;
    setIsSavingPaste(true);
    try {
      await WorkoutAssignment.assignWorkout(userDoc, copiedWorkout, {
        startDate: moment.utc(dateStr).startOf('day'),
        endDate: moment.utc(dateStr).endOf('day'),
        coachId,
        productId,
        productName,
      });
    } catch (error) {
      showToast(format(texts.pasteError, { msg: error.message }), { error: true });
      Sentry.captureException(error, {
        extra: {
          userId: userDoc.id,
          workoutId: copiedWorkout.id,
          date: dateStr,
        },
      });
      return;
    } finally {
      if (isComponentMountedRef.current) {
        setIsSavingPaste(false);
      }
    }
    // Outside the write's try: a logging failure after a saved write must not tell the coach to paste again.
    showToast(texts.pasteSuccess);
    try {
      logCoachingActivity(CoachingActivity.ASSIGNED_WORKOUT, {
        workoutId: copiedWorkout.id,
        clientId: userDoc.id,
        ...(productId && { productId }),
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [
    isComponentMountedRef,
    copiedWorkout,
    showToast,
    userDoc,
    coachId,
    logCoachingActivity,
    soleActiveProductId,
    products,
  ]);

  const onWorkoutAssignmentClick = useCallback((workoutAssignment) => {
    setMobileUrl(
      `${config.mobileAppURLV2}/u/${user}/${WORKOUT_ASSIGNMENT}/${workoutAssignment.id}`
      + `/${WORKOUT_ASSIGNMENT_DETAILS}?coachId=${coachId}`,
    );
    setSelectedWorkout(workoutAssignment);
    setShowWorkoutDetailsPopup(true);
  }, [
    user,
    coachId,
  ]);

  const editWorkoutAssignment = useCallback(async (workoutAssignment, closePopup) => {
    closePopup();
    setSelectedWorkout(workoutAssignment);
    setShowWorkoutEditorModal(true);
  }, []);

  /**
   * Marks the selected workout assignment as done.
   * This will force finish the gameplay session and mark the workout assignment as partially completed.
   * The feed view and streak documents will get automatically updated with the latest data after the update.
   */
  const onMarkAsDone = useCallback(async () => {
    try {
      setShowLoading(true);
      const {
        id: workoutAssignmentId,
        user: userId,
      } = selectedWorkout;

      const gameplayCol = await GameplaySession.getLatestGameplaySessionCollection(userId, workoutAssignmentId);
      if (!gameplayCol.hasDocs) {
        throw new Error(texts.noGameplay);
      }
      const gameplaySession = gameplayCol.docs[0];
      await gameplaySession.forceFinish();
      await selectedWorkout.forceCompleteAssignment(gameplaySession.ref.path, loggedInUserId);
      if (isComponentMountedRef.current) {
        setSelectedWorkout(null);
        setShowCompletionModal(false);
        setShowLoading(false);
      }
      showToast(texts.completionSuccess);
    } catch (err) {
      showToast(format(texts.completionError, { err }), { error: true });
    }
  }, [
    isComponentMountedRef,
    showToast,
    loggedInUserId,
    selectedWorkout,
  ]);

  const onMarkAsDoneClick = useCallback((workoutAssignment, closePopup) => {
    closePopup();
    setSelectedWorkout(workoutAssignment);
    setShowCompletionModal(true);
  }, []);

  /**
   * Saves the coach's edit of a workout that is scheduled but not sent yet. The edit
   * is stored on the scheduled assignment and swapped in when it fires, so the source
   * workout — and every other client it is assigned to — is left untouched.
   */
  const saveScheduledWorkoutDraft = useCallback(async (workoutContent) => {
    const scheduledAssignmentDoc = pendingScheduledDocs.find(
      ({ id }) => id === selectedDraft.scheduledAssignmentId,
    );

    // The assignment fired (or was cancelled) while the editor was open, so it is gone
    // from the pending set and the edit has nowhere to land.
    if (!scheduledAssignmentDoc) {
      throw new UserFacingError(texts.scheduledWorkoutNotEditable);
    }

    try {
      await scheduledAssignmentDoc.setWorkoutOverride(selectedDraft.date, selectedDraft.workoutId, workoutContent);
      logCoachingActivity(CoachingActivity.UPDATED_ASSIGNED_WORKOUT, {
        workoutId: selectedDraft.workoutId,
        clientId: user,
      });
    } catch (error) {
      /*
        Security rules allow this write only while the assignment is PENDING, and the
        coach and payload shape are right by construction here — so a rejection means
        the assignment stopped being pending between loading the drafts and saving.
        Reported as that rather than as a raw Firestore permission error.
      */
      Sentry.captureException(error, {
        extra: {
          scheduledAssignmentId: selectedDraft.scheduledAssignmentId,
          date: selectedDraft.date,
        },
      });
      throw new UserFacingError(texts.scheduledWorkoutNotEditable);
    }
  }, [
    pendingScheduledDocs,
    selectedDraft,
    logCoachingActivity,
    user,
  ]);

  const renderEventContent = useCallback((eventInfo) => {
    // Assignments carry no extendedProps at all, so this cannot assume the object is there.
    const { draft } = eventInfo.event.extendedProps ?? {};
    if (draft) {
      return (
        <ScheduledDraftEventContent
          draft={draft}
          scheduledForText={scheduledSendTimes[draft.scheduledAssignmentId]}
          onEditDraft={setSelectedDraft}
        />
      );
    }
    const workoutAssignment = workoutAssignments.docs.find((w) => w.id === eventInfo.event.id);
    if (!workoutAssignment) return null;
    return (
      <EventContent
        workoutAssignment={workoutAssignment}
        onWorkoutAssignmentClick={onWorkoutAssignmentClick}
        editWorkoutAssignment={editWorkoutAssignment}
        deleteWorkoutAssignment={deleteWorkoutAssignment}
        copyWorkoutAssignment={copyWorkoutAssignment}
        showProductName={showProductName}
        onMarkAsDone={onMarkAsDoneClick}
      />
    );
  }, [
    deleteWorkoutAssignment,
    copyWorkoutAssignment,
    showProductName,
    workoutAssignments.docs,
    onWorkoutAssignmentClick,
    editWorkoutAssignment,
    onMarkAsDoneClick,
    scheduledSendTimes,
  ]);

  const workoutCompletionStatus = useMemo(() => (
    workoutAssignments.docs.reduce((acc, doc) => {
      acc[doc.id] = doc?.isCompleted === true;
      return acc;
    }, {})
  ), [workoutAssignments.docs]);

  // Drafts are not assignments yet, so they carry no start/end date to move — they
  // land wherever the assignment is going to place them, and cannot be dragged.
  const draftEvents = useMemo(() => (
    showScheduledWorkouts
      ? scheduledDrafts.map((draft) => ({
        id: draft.id,
        title: draft.workoutContent?.name,
        // The date-only string keeps the predicted day intact: a Date built from it
        // would be UTC midnight, which lands on the previous day for coaches west of
        // UTC. The assignment path avoids the same trap in `momentStartDate`.
        start: draft.date,
        allDay: true,
        editable: false,
        eventDurationEditable: false,
        extendedProps: { draft },
      }))
      : []
  ), [scheduledDrafts, showScheduledWorkouts]);

  // Draft events are built ready for the calendar, while workout assignments are
  // the raw documents and still need mapping.
  const transformData = useCallback((eventInput) => {
    if (eventInput.extendedProps?.draft) {
      return eventInput;
    }
    return {
      id: eventInput.id,
      title: eventInput.name,
      start: eventInput.momentStartDate.toDate(),
      allDay: true,
      editable: !workoutCompletionStatus[eventInput.id],
      eventDurationEditable: false,
    };
  }, [workoutCompletionStatus]);

  const onDateClick = useCallback(({ dateStr, isProgram = false, isTrack = false }) => {
    setSelectedDate(dateStr);
    if (isTrack) {
      setShowTrackAssignmentModal(true);
    } else if (isProgram) {
      setShowProgramAssignmentModal(true);
    } else {
      setShowWorkoutAssignmentModal(true);
    }
  }, []);

  const handleCellMount = useCallback(({ el, date }) => {
    const cellDiv = el;
    cellDiv.onmouseenter = () => setHoverDate(date);
    cellDiv.onmouseleave = () => setHoverDate(null);
  }, []);

  const updateWorkoutAssignmentDates = useCallback(async (id, eventStartDate) => {
    setShowLoading(true);
    const workoutAssigmentDoc = workoutAssignments.docs.find((w) => w.id === id);
    if (workoutAssigmentDoc) {
      await workoutAssigmentDoc.ref.update({
        startDate: moment.utc(eventStartDate).startOf('day').toDate(),
        endDate: moment.utc(eventStartDate).endOf('day').toDate(),
      });
    }
    if (isComponentMountedRef.current) {
      showToast(texts.workoutUpdated);
      setShowLoading(false);
    }
  }, [
    workoutAssignments.docs,
    showToast,
    isComponentMountedRef,
  ]);

  const isImmovableWorkout = useCallback((eventId) => {
    const doc = workoutAssignments.docs.find((w) => w.id === eventId);
    return doc?.isCompleted === true;
  }, [workoutAssignments.docs]);

  const handleEventDragStop = useCallback((info) => {
    if (isImmovableWorkout(info.event.id)) {
      showToast(texts.completedWorkoutMove, { warning: true });
    }
  }, [isImmovableWorkout, showToast]);

  const onBeforeDelete = useCallback((ids) => {
    // workoutAssignments carries collection metadata alongside docs — spread prev to preserve it,
    // then replace only the docs array with the filtered set.
    setWorkoutAssignments((prev) => ({
      ...prev,
      docs: prev.docs.filter((d) => !ids.includes(d.id)),
    }));
  }, []);

  const onModalClose = useCallback(() => {
    setShowDeleteWorkoutModal(false);
    setFutureLikeAssignments([]);
    setSelectedWorkout(null);
  }, []);

  return (
    <>
      {showWorkoutAssignmentModal && (
        <WorkoutAssignmentModal
          showModal={showWorkoutAssignmentModal}
          onClose={() => setShowWorkoutAssignmentModal(false)}
          userDoc={userDoc}
          selectedDate={selectedDate}
        />
      )}
      {showDeleteWorkoutModal && (
        <WorkoutDeletionModal
          showModal={showDeleteWorkoutModal}
          onClose={onModalClose}
          selectedWorkout={selectedWorkout}
          workoutAssignments={workoutAssignments}
          futureLikeAssignments={futureLikeAssignments}
          onBeforeDelete={onBeforeDelete}
        />
      )}
      {showProgramAssignmentModal && (
        <ProgramAssignmentModal
          showModal={showProgramAssignmentModal}
          onClose={() => setShowProgramAssignmentModal(false)}
          userDoc={userDoc}
          selectedDate={selectedDate}
        />
      )}
      {showTrackAssignmentModal && (
        <TrackAssignmentModal
          showModal={showTrackAssignmentModal}
          onClose={() => setShowTrackAssignmentModal(false)}
          userDoc={userDoc}
          selectedDate={selectedDate}
        />
      )}

      {showWorkoutEditorModal && (
        <WorkoutEditorModal
          showModal={showWorkoutEditorModal}
          workoutAssignment={selectedWorkout}
          editorAction={ActionType.UPDATE_WORKOUT_ASSIGNMENT}
          onSave={() => { setShowWorkoutEditorModal(false); setSelectedWorkout(null); }}
          onClose={() => { setShowWorkoutEditorModal(false); setSelectedWorkout(null); }}
        />
      )}
      {!!selectedDraft && (
        <WorkoutEditorModal
          showModal
          scheduledWorkoutDraft={selectedDraft}
          editorAction={ActionType.UPDATE_SCHEDULED_WORKOUT}
          onSaveScheduledWorkoutDraft={saveScheduledWorkoutDraft}
          onSave={() => setSelectedDraft(null)}
          onClose={() => setSelectedDraft(null)}
        />
      )}
      <ConfirmDialog
        isOpen={showCompletionModal}
        onConfirm={onMarkAsDone}
        onCancel={() => setShowCompletionModal(false)}
        dialogTexts={{
          title: texts.warningModal.title,
          content: format(texts.warningModal.description, {
            workout: selectedWorkout?.name,
            startedOn: moment(selectedWorkout?.workoutStartedDate?.toDate())
              ?.format(DateFormat.DATE_FORMAT_COMMA_TH),
          }),
        }}
      />
      <ConfirmDialog
        isOpen={!!scheduledToCancel}
        onConfirm={handleCancelScheduledAssignment}
        onCancel={() => setScheduledToCancel(null)}
        dialogTexts={{
          title: texts.cancelScheduledModal.title,
          content: format(texts.cancelScheduledModal.description, {
            name: scheduledToCancel?.displayName || '',
          }),
        }}
      />
      {pendingScheduledDocs.length > 0 && (
        <ScheduledBannerContainer>
          {pendingScheduledDocs.map((doc) => (
            <ScheduledRow key={doc.id}>
              <ScheduledRowIcon>
                <ScheduleOutlined fontSize="small" />
              </ScheduledRowIcon>
              <ScheduledRowMessage>
                <ScheduledRowName>{doc.displayName}</ScheduledRowName>
                {getScheduledBannerMessage(draftCountByAssignmentId[doc.id])}
              </ScheduledRowMessage>
              <ScheduledRowType>{doc.type}</ScheduledRowType>
              {!!doc.productName && <ScheduledRowType>{doc.productName}</ScheduledRowType>}
              {doc.isOnHold && <OnHoldBadge>{texts.onHoldBadgeLabel}</OnHoldBadge>}
              <ScheduledRowTime>
                {format(texts.scheduledBanner.sendsAt, { scheduledFor: scheduledSendTimes[doc.id] })}
              </ScheduledRowTime>
              <ScheduledDeleteButton onClick={() => setScheduledToCancel(doc)}>
                <DeleteOutline fontSize="small" />
              </ScheduledDeleteButton>
            </ScheduledRow>
          ))}
        </ScheduledBannerContainer>
      )}
      {scheduledDrafts.length > 0 && (
        <CalendarControlsRow>
          <FormControlLabel
            control={(
              <Checkbox
                checked={showScheduledWorkouts}
                onChange={(event) => setShowScheduledWorkouts(event.target.checked)}
                size="small"
              />
            )}
            label={texts.showScheduledWorkouts}
          />
        </CalendarControlsRow>
      )}
      <CalendarWrapper>
        <ProgramInfo
          workoutAssignments={workoutAssignments}
          coachPrograms={coachPrograms}
          visibleDateRange={visibleDateRange}
        />
        <FullCalendar
          buttonText={{
            today: texts.today,
          }}
          headerToolbar={{
            right: 'prev,today,next',
          }}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          datesSet={(arg) => setVisibleDateRange(arg)}
          dayCellContent={(dayData) => (
            <CalendarDayView
              showPasteButton={!!copiedWorkout}
              selectedDate={hoverDate}
              dayData={dayData}
              onWorkoutAssignmentClick={(date) => onDateClick({ dateStr: date })}
              onProgramAssignmentClick={(date) => onDateClick({ dateStr: date, isProgram: true })}
              onTrackAssignmentClick={(date) => onDateClick({ dateStr: date, isTrack: true })}
              onWorkoutPasteClick={(date) => {
                onPasteWorkout(date);
              }}
            />
          )}
          events={[...workoutAssignments.docs, ...draftEvents]}
          eventContent={renderEventContent}
          eventDataTransform={transformData}
          dayCellDidMount={handleCellMount}
          selectable
          eventAllow={(dropInfo, draggedEvent) => !isImmovableWorkout(draggedEvent.id)}
          eventDragStop={handleEventDragStop}
          eventDrop={
            (info) => updateWorkoutAssignmentDates(info.event.id, info.event.startStr)
          }
          eventReceive={
            (info) => updateWorkoutAssignmentDates(info.event.id, info.event.startStr)
          }
        />
      </CalendarWrapper>
      {showWorkoutDetailsPopup && (
        <MobileViewModal
          showModal={showWorkoutDetailsPopup}
          onClose={() => setShowWorkoutDetailsPopup(false)}
          mobileUrl={mobileUrl}
        />
      )}
      <LoadingOverlay
        isLoading={showLoading || isSavingPaste}
        loadingText={isSavingPaste ? texts.pasteSaving : ''}
      />
    </>
  );
};

WorkoutCalendar.propTypes = {
  userDoc: PropTypes.instanceOf(User),
  user: PropTypes.string.isRequired,
  coachId: PropTypes.string.isRequired,
};

WorkoutCalendar.defaultProps = {
  userDoc: null,
};

export default compose(
  observer,
)(WorkoutCalendar);
